/**
 * Fight rules and numbers (docs/fight.md). Times are in seconds, distances in world units
 * (arena image pixels). Values without a comment come from the playtested prototype.
 */
export const FIGHT = {
  /** Simulation steps per second. Everything in the sim advances by 1 / tickRate. */
  tickRate: 60,

  rounds: 3,
  roundSeconds: 60,
  /** "ROUND n · FIGHT!" before fighters can move. */
  introSeconds: 1.4, // placeholder
  /** Pause after the round clock hits 0 before the break or decision. */
  timeUpSeconds: 0.7,
  breakSeconds: 5, // placeholder
  /**
   * Health recovered as each break between rounds starts, in tenths: 20 health, about 6% of
   * 325 (owner, 2026-10-02: "slightly"). The Second breath perk adds its own. The break card
   * shows what each fighter got back.
   */
  healthRecoveryBetweenRounds: 200, // placeholder
  /** Time between a KO and the end of the fight. */
  koSeconds: 1.7,
  /** How long the decision card shows before the fight is over. */
  decisionSeconds: 2.5, // placeholder

  /** Start positions relative to the octagon centre: ±x, +y. */
  startOffsetX: 300,
  startOffsetY: 60,

  /** Gameplay size S = 165 × 1.1. Rendered size is a separate client setting (T14). */
  size: 181.5,
  radiusFactor: 0.42,
  pushApartRadii: 1.9,
  fenceInsetRadii: 0.72,
  fenceRestitution: 0.6,
  facingDeadzone: 4,

  /** Health in tenths (T15): 3250 = 325 health. Owner feel test 2026-09-28: 300–350 felt right. */
  maxHealth: 3250, // placeholder

  moveSpeed: 430,
  accel: 14,
  blockSpeedMul: 0.35,
  attackSpeedMul: 0.3,

  knockbackDecay: 7,
  koKnockbackMul: 1.4,

  staminaMax: 100,
  // Owner feel test 2026-09-29: stamina never ran out (was 22 and 8 per second).
  staminaRegen: 12, // placeholder
  staminaRegenBlocking: 4, // placeholder
  blockedHitStaminaCost: 10,

  meterMax: 100,
  meterPassive: 1.2,
  meterAttackerGain: 1.6,
  meterDefenderGain: 0.8,

  inputBuffer: 0.18,

  strike: {
    cost: 11, // placeholder (was 9)
    duration: 0.28,
    hitStart: 0.09,
    hitEnd: 0.18,
    /** reach = 2R + reachExtra × S */
    reachExtra: 0.42,
    damage: 70,
    knockback: 330,
    stun: 0.22,
  },

  grapple: {
    cost: 26, // placeholder (was 22)
    duration: 0.55,
    dashStart: 0.1,
    dashEnd: 0.32,
    dashSpeed: 1050,
    contactRadii: 2.08,
    /** Both fighters freeze for this long between the grab and the slam (F4). */
    grabFreeze: 0.12,
    damage: 120,
    knockback: 180,
    down: 0.95,
    /** A strike younger than this at contact counters the grapple. */
    counterWindow: 0.22,
    counterStun: 0.5,
  },

  defend: {
    strikeReduction: 0.8,
    signatureReduction: 0.5,
    blockedKnockbackMul: 0.4,
    attackerBounce: 140,
  },

  signature: {
    duration: 0.82,
    windup: 0.38,
    dashEnd: 0.64,
    dashSpeed: 1400,
    contactRadii: 2.12,
    damage: 240,
    knockback: 980,
    stun: 0.55,
    keepSpeed: 150,
  },

  /**
   * Rocked (D13, docs/fight.md §5): a clean hit that leaves a fighter at or below this share of
   * max health rocks them, at most once per round. Rocked: no strikes, grapples or signature,
   * slower movement; blocking and moving still work.
   */
  rocked: {
    healthShare: 0.15, // placeholder
    /** Seconds; time spent down or grabbed doesn't count. */
    duration: 2.2, // placeholder
    speedMul: 0.55, // placeholder
    /** Damage a rocked fighter takes (they can't brace). Blocking still reduces it. */
    damageTakenMul: 1.3, // placeholder
  },

  /**
   * Reads (D14, docs/fight.md §5): winning the rock-paper-scissors exchange. Blocking a strike or
   * signature, or countering a grapple with a strike, opens a punish window: the next strike that
   * lands clean in it is a punish. A takedown on a blocking fighter is a punish by itself.
   */
  read: {
    window: 0.9, // placeholder
    damageMul: 1.5, // placeholder
    knockbackMul: 1.3, // placeholder
  },

  slowMo: {
    signature: 0.35,
    ko: 0.25,
    /** Time scale recovers by this much per second of real time. */
    recovery: 0.9,
  },
} as const;

export type FightConfig = typeof FIGHT;
